/**
 * Drag handle for resizing elements.
 *
 * Thin vertical bar that appears on hover.
 * Expanded hit area for easier grabbing.
 *
 * Uses dual-strategy visual feedback:
 * - Dark mode: Warm layered glow (lamp effect)
 * - Light mode: Tactile depth (warm shadow)
 */

import * as stylex from "@stylexjs/stylex";
import * as React from "react";
import { useEffect, useState } from "react";

import { duration, easing } from "../tokens.stylex";

interface ThemeColors {
  core: string;
  glow: string;
}

function lerpColor(from: string, to: string, t: number): string {
  const fromParts = from.split(", ").map(Number);
  const toParts = to.split(", ").map(Number);
  const r1 = fromParts[0] ?? 0;
  const g1 = fromParts[1] ?? 0;
  const b1 = fromParts[2] ?? 0;
  const r2 = toParts[0] ?? 0;
  const g2 = toParts[1] ?? 0;
  const b2 = toParts[2] ?? 0;
  const r = Math.round(r1 + (r2 - r1) * t);
  const g = Math.round(g1 + (g2 - g1) * t);
  const b = Math.round(b1 + (b2 - b1) * t);
  return `${r}, ${g}, ${b}`;
}

function getThemeColors(
  isDark: boolean,
  canExpand: boolean,
  expandProgress: number
): ThemeColors {
  const normalCore = isDark ? "255, 255, 255" : "255, 249, 244";
  const normalGlow = isDark ? "232, 136, 153" : "212, 168, 85";
  const blockedCore = isDark ? "255, 100, 100" : "220, 80, 80";
  const blockedGlow = "220, 60, 60";

  if (!canExpand && expandProgress > 0) {
    return {
      core: lerpColor(normalCore, blockedCore, expandProgress),
      glow: lerpColor(normalGlow, blockedGlow, expandProgress),
    };
  }
  return { core: normalCore, glow: normalGlow };
}

interface ResizeVisuals {
  coreAlpha: number;
  glowAlpha: number;
  lineHeight: number;
  lineWidth: number;
  glowNear: number;
  glowFar: number;
}

function getResizeVisuals(
  isResizing: boolean,
  containerHeight: number
): ResizeVisuals {
  if (isResizing) {
    return {
      coreAlpha: 1,
      glowAlpha: 0.4,
      lineHeight: Math.min(containerHeight, 60),
      lineWidth: 3,
      glowNear: 15,
      glowFar: 30,
    };
  }
  return {
    coreAlpha: 0.5,
    glowAlpha: 0.35,
    lineHeight: Math.min(containerHeight, 48),
    lineWidth: 2,
    glowNear: 3,
    glowFar: 8,
  };
}

interface CenterYParams {
  cursorY: number | null;
  fallbackY: number | null;
  rect: DOMRect | undefined;
  containerHeight: number;
  lineHeight: number;
}

function computeCenterY(params: CenterYParams): number {
  const { cursorY, fallbackY, rect, containerHeight, lineHeight } = params;
  const effectiveCursorY = cursorY ?? fallbackY;
  const localY =
    effectiveCursorY !== null && rect !== undefined
      ? effectiveCursorY - rect.top
      : containerHeight / 2;
  const clampedY = Math.max(0, Math.min(containerHeight, localY));
  return Math.max(
    lineHeight / 2,
    Math.min(containerHeight - lineHeight / 2, clampedY)
  );
}

const linePulse = stylex.keyframes({
  "0%": { transform: "translateX(-50%) translateY(-50%) scale(1)" },
  "50%": { transform: "translateX(-50%) translateY(-50%) scale(1.02)" },
  "100%": { transform: "translateX(-50%) translateY(-50%) scale(1)" },
});

const HANDLE_WIDTH = 4;

const styles = stylex.create({
  container: {
    position: "absolute",
    top: 0,
    left: 0,
    width: 12,
    height: "100%",
    cursor: "col-resize",
    zIndex: 20,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    transform: "translateX(-50%)",
  },
  handle: {
    width: HANDLE_WIDTH,
    minWidth: HANDLE_WIDTH,
    flexShrink: 0,
    height: "100%",
    backgroundColor: "transparent",
    borderRadius: HANDLE_WIDTH,
    opacity: 0,
    position: "relative",
    zIndex: 2,
    transitionProperty: "box-shadow, opacity",
    transitionDuration: duration.fast,
    transitionTimingFunction: easing.default,
  },
  line: {
    position: "absolute",
    left: "50%",
    transformOrigin: "center",
    willChange: "transform, opacity, height",
    pointerEvents: "none",
    zIndex: 3,
  },
  lineResizing: {
    animationName: linePulse,
    animationDuration: "1400ms",
    animationTimingFunction: easing.easeInOut,
    animationIterationCount: "infinite",
    animationFillMode: "both",
  },
  lineReducedMotion: {
    "@media (prefers-reduced-motion: reduce)": {
      animationName: "none",
      animationDuration: "0ms",
      animationIterationCount: "1",
    },
  },
});

function useResolvedTheme(): "light" | "dark" {
  const [theme, setTheme] = useState<"light" | "dark">(() => {
    if (typeof document === "undefined") {
      return "dark";
    }
    return (
      (document.documentElement.dataset.theme as "light" | "dark") || "dark"
    );
  });

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const newTheme = document.documentElement.dataset.theme as
        | "light"
        | "dark";
      if (newTheme && newTheme !== theme) {
        setTheme(newTheme);
      }
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });

    return () => observer.disconnect();
  }, [theme]);

  return theme;
}

interface ResizeHandleProps {
  onMouseDown: (e: React.MouseEvent) => void;
  isResizing?: boolean;
  expandProgress?: number;
  cursorX?: number | null;
  cursorY?: number | null;
  canExpand?: boolean;
}

function ResizeHandle({
  onMouseDown,
  isResizing = false,
  expandProgress = 0,
  cursorX = null,
  cursorY = null,
  canExpand = true,
}: ResizeHandleProps) {
  const [localCursorY, setLocalCursorY] = useState<number | null>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const lastCursorXRef = React.useRef<number | null>(null);
  const lastCursorYRef = React.useRef<number | null>(null);
  const resolvedTheme = useResolvedTheme();
  const isDark = resolvedTheme === "dark";

  // During resize: use cursorY prop (tracked globally by parent)
  // During hover: use localCursorY (tracked locally via mouse events)
  const activeCursorY = isResizing ? cursorY : localCursorY;

  if (cursorX !== null) {
    lastCursorXRef.current = cursorX;
  }

  if (activeCursorY !== null) {
    lastCursorYRef.current = activeCursorY;
  }

  // Simple handlers - no guards needed because pointer-events: none during resize
  // prevents these from firing while the container is moving
  const handleMouseMove = (e: React.MouseEvent) => {
    lastCursorXRef.current = e.clientX;
    setLocalCursorY(e.clientY);
  };

  const handleMouseEnter = (e: React.MouseEvent) => {
    lastCursorXRef.current = e.clientX;
    setLocalCursorY(e.clientY);
  };

  const handleMouseLeave = () => {
    setLocalCursorY(null);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    lastCursorXRef.current = e.clientX;
    lastCursorYRef.current = e.clientY;
    onMouseDown(e);
  };

  const wasResizingRef = React.useRef(false);
  useEffect(() => {
    if (wasResizingRef.current && !isResizing) {
      const rect = containerRef.current?.getBoundingClientRect();
      const lastCursorX = lastCursorXRef.current;
      const lastCursorY = lastCursorYRef.current;

      const isWithinHandle =
        rect !== undefined &&
        lastCursorX !== null &&
        lastCursorY !== null &&
        lastCursorX >= rect.left &&
        lastCursorX <= rect.right &&
        lastCursorY >= rect.top &&
        lastCursorY <= rect.bottom;

      setLocalCursorY(isWithinHandle ? lastCursorY : null);
    }
    wasResizingRef.current = isResizing;
  }, [isResizing]);

  const getLineStyle = (): React.CSSProperties => {
    const rect = containerRef.current?.getBoundingClientRect();
    const containerHeight = rect?.height ?? 400;

    const { core: coreColor, glow: glowColor } = getThemeColors(
      isDark,
      canExpand,
      expandProgress
    );
    const { coreAlpha, glowAlpha, lineHeight, lineWidth, glowNear, glowFar } =
      getResizeVisuals(isResizing, containerHeight);
    const centerY = computeCenterY({
      cursorY: activeCursorY,
      fallbackY: lastCursorYRef.current,
      rect,
      containerHeight,
      lineHeight,
    });

    const coreGradient = `linear-gradient(to bottom,
      rgba(${coreColor}, 0) 0%,
      rgba(${coreColor}, ${coreAlpha}) 50%,
      rgba(${coreColor}, 0) 100%)`;

    const glowGradient = `linear-gradient(to bottom,
      rgba(${glowColor}, 0) 0%,
      rgba(${glowColor}, ${glowAlpha}) 50%,
      rgba(${glowColor}, 0) 100%)`;

    const opacity = isResizing || activeCursorY !== null ? 1 : 0;
    const visualTransition = isResizing
      ? "none"
      : "height 180ms ease-out, width 180ms ease-out, box-shadow 180ms ease-out, background 180ms ease-out, opacity 150ms ease-out";

    return {
      width: lineWidth,
      height: lineHeight,
      top: centerY,
      transform: "translateX(-50%) translateY(-50%)",
      borderRadius: lineWidth,
      opacity,
      background: `${coreGradient}, ${glowGradient}`,
      boxShadow: `
        0 0 ${glowNear}px rgba(${coreColor}, ${coreAlpha * 0.5}),
        0 0 ${glowFar}px rgba(${glowColor}, ${glowAlpha * 0.6})
      `,
      transition: visualTransition,
    };
  };

  const lineStyle = getLineStyle();

  // During resize, disable pointer events on container to prevent unstable
  // hover events as the element moves. Parent tracks cursor globally via cursorY prop.
  const containerStyle: React.CSSProperties | undefined = isResizing
    ? { pointerEvents: "none" }
    : undefined;

  return (
    <div
      ref={containerRef}
      {...stylex.props(styles.container)}
      aria-hidden="true"
      onMouseDown={handleMouseDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseMove={handleMouseMove}
      style={containerStyle}
    >
      <div
        {...stylex.props(
          styles.line,
          isResizing && styles.lineResizing,
          styles.lineReducedMotion
        )}
        data-resize-line
        style={lineStyle}
      />
      <div {...stylex.props(styles.handle)} data-resize-handle />
    </div>
  );
}

export const resizeHandle = styles;

export { ResizeHandle, type ResizeHandleProps };
